const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

// Helper to get token from localStorage
const getToken = () => localStorage.getItem('tmg_token');

// Helper to handle API responses
const parseResponseData = async (response) => {
  const data = await response.json();

  if (!response.ok) {
    throw { response, data };
  }

  return data.data;
};

const handleResponse = async (response) => {
  try {
    return await parseResponseData(response);
  } catch (error) {
    const { response: failedResponse, data } = error || {};
    if (failedResponse?.status === 401) {
      localStorage.removeItem('tmg_token');
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('tmg:session-expired', {
          detail: { message: data?.message || 'Session expired. Please log in again.' }
        }));
      }
    }
    throw new Error(data?.message || 'An error occurred');
  }
};

const handleAuthResponse = async (response) => {
  try {
    return await parseResponseData(response);
  } catch (error) {
    const { data } = error || {};
    throw new Error(data?.message || 'An error occurred');
  }
};

// Helper to make authenticated requests
const authenticatedFetch = (url, options = {}) => {
  const token = getToken();
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const incomingHeaders = options.headers || {};
  const headers = {
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...incomingHeaders,
  };

  if (!isFormData && !('Content-Type' in headers)) {
    headers['Content-Type'] = 'application/json';
  }

  return fetch(url, {
    ...options,
    headers,
  });
};

// Retry a request on transient NETWORK failures only (e.g. "Failed to fetch" when a
// connection drops mid-upload during a large multi-receipt claim). Real HTTP errors
// thrown by handleResponse (quota, validation, file-too-large) are surfaced immediately
// and never retried.
const isTransientNetworkError = (error) =>
  error instanceof TypeError ||
  /failed to fetch|networkerror|network error|load failed|connection|err_network/i.test(
    String(error?.message || '')
  );

const withNetworkRetry = async (fn, { retries = 3, baseDelayMs = 800 } = {}) => {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (!isTransientNetworkError(error) || attempt === retries) throw error;
      // Linear backoff: 0.8s, 1.6s, 2.4s — gives a flaky connection time to recover.
      await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
    }
  }
  throw lastError;
};

// Auth APIs
export const authAPI = {
  requestOTP: async (identifier, method) => {
    // identifier can be email or phone
    const isEmail = identifier.includes('@');
    const body = isEmail
      ? { email: identifier, method }
      : { phone: identifier, method };

    const response = await fetch(`${API_BASE_URL}/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handleAuthResponse(response);
  },

  verifyOTP: async (identifier, otp) => {
    // identifier can be email or phone
    const isEmail = identifier.includes('@');
    const body = isEmail
      ? { email: identifier, otp }
      : { phone: identifier, otp };

    const response = await fetch(`${API_BASE_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await handleAuthResponse(response);

    // Store token
    if (data.token) {
      localStorage.setItem('tmg_token', data.token);
    }

    return data;
  },

  logout: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
    });
    localStorage.removeItem('tmg_token');
    return handleResponse(response);
  },
};

// User APIs
export const userAPI = {
  getCurrentUser: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/me`);
    return handleResponse(response);
  },

  getAllUsers: async (page = 1, limit = 100, options = {}) => {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });

    if (options.search) params.set('search', options.search);
    if (options.entity) params.set('entity', options.entity);
    if (options.role) params.set('role', options.role);

    const response = await authenticatedFetch(
      `${API_BASE_URL}/users?${params.toString()}`
    );
    return handleResponse(response);
  },

  createUser: async (userData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/users`, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  importUsers: async ({ mappings, rows }) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/import`, {
      method: 'POST',
      body: JSON.stringify({ mappings, rows }),
    });
    return handleResponse(response);
  },

  updateUser: async (userId, userData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  unlinkTelegram: async (userId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/users/${userId}/telegram-link`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// Claim APIs
export const claimAPI = {
  getMyClaims: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/my-claims`);
    return handleResponse(response);
  },

  getPendingApprovals: async (approverType, options = {}) => {
    const params = new URLSearchParams();
    params.set('approver_type', approverType);
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    if (options.entity) params.set('entity', options.entity);
    if (options.include_history) params.set('include_history', '1');
    const response = await authenticatedFetch(
      `${API_BASE_URL}/claims/pending-approval?${params.toString()}`
    );
    return handleResponse(response);
  },

  getReportClaims: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    if (options.entity) params.set('entity', options.entity);
    const response = await authenticatedFetch(
      `${API_BASE_URL}/claims/report?${params.toString()}`
    );
    return handleResponse(response);
  },

  getClaimDetails: async (claimId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}`);
    return handleResponse(response);
  },

  createDraftClaim: async (claimData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims`, {
      method: 'POST',
      body: JSON.stringify(claimData),
    });
    return handleResponse(response);
  },

  submitClaim: async (claimId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}/submit`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  approveClaim: async (claimId, approverType, reason = '') => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}/approve`, {
      method: 'POST',
      body: JSON.stringify({ approver_type: approverType, reason }),
    });
    return handleResponse(response);
  },

  rejectClaim: async (claimId, approverType, reason) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}/reject`, {
      method: 'POST',
      body: JSON.stringify({ approver_type: approverType, reason }),
    });
    return handleResponse(response);
  },

  returnClaim: async (claimId, reason) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}/return`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return handleResponse(response);
  },

  updateClaim: async (claimId, claimData) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}`, {
      method: 'PUT',
      body: JSON.stringify(claimData),
    });
    return handleResponse(response);
  },

  resubmitClaim: async (claimId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/claims/${claimId}/resubmit`, {
      method: 'POST',
    });
    return handleResponse(response);
  },
};

export const auditAPI = {
  getLogs: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.claim_id) params.set('claim_id', String(options.claim_id));
    if (options.claim_number) params.set('claim_number', options.claim_number);
    if (options.user_id) params.set('user_id', String(options.user_id));
    if (options.action) params.set('action', options.action);
    if (options.start_date) params.set('start_date', options.start_date);
    if (options.end_date) params.set('end_date', options.end_date);
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    const response = await authenticatedFetch(`${API_BASE_URL}/audit/logs?${params.toString()}`);
    return handleResponse(response);
  },
  getPublicLogs: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.key) params.set('key', options.key);
    if (options.claim_id) params.set('claim_id', String(options.claim_id));
    if (options.claim_number) params.set('claim_number', options.claim_number);
    if (options.user_id) params.set('user_id', String(options.user_id));
    if (options.action) params.set('action', options.action);
    if (options.start_date) params.set('start_date', options.start_date);
    if (options.end_date) params.set('end_date', options.end_date);
    if (options.page) params.set('page', String(options.page));
    if (options.limit) params.set('limit', String(options.limit));
    const response = await fetch(`${API_BASE_URL}/audit/public-logs?${params.toString()}`);
    return handleResponse(response);
  },
};

// Quota APIs
export const quotaAPI = {
  getUserQuotas: async (userId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/quotas/user/${userId}`);
    return handleResponse(response);
  },
  setQuota: async (data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/quotas`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Company APIs
export const companyAPI = {
  getCompanies: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/companies`);
    return handleResponse(response);
  },

  createCompany: async (data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/companies`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateCompany: async (id, data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Department APIs
export const departmentAPI = {
  getDepartments: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/departments`);
    return handleResponse(response);
  },

  createDepartment: async (data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/departments`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateDepartment: async (id, data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/departments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Expense GL Code APIs
export const expenseGlCodeAPI = {
  getExpenseGlCodes: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.entity) params.set('entity', options.entity);
    if (options.include_inactive) params.set('include_inactive', '1');
    const response = await authenticatedFetch(
      `${API_BASE_URL}/expense-gl-codes?${params.toString()}`
    );
    return handleResponse(response);
  },

  createExpenseGlCode: async (data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/expense-gl-codes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateExpenseGlCode: async (id, data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/expense-gl-codes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Company Expense APIs
export const expenseAPI = {
  getExpenses: async (options = {}) => {
    const params = new URLSearchParams();
    if (options.entity) params.set('entity', options.entity);
    if (options.claim_type) params.set('claim_type', options.claim_type);
    if (options.include_inactive) params.set('include_inactive', '1');
    const response = await authenticatedFetch(
      `${API_BASE_URL}/expenses?${params.toString()}`
    );
    return handleResponse(response);
  },

  createExpense: async (data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/expenses`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },

  updateExpense: async (id, data) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/expenses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handleResponse(response);
  },
};

// Webhook APIs
export const webhookAPI = {
  getWebhooks: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/webhooks`);
    return handleResponse(response);
  },

  createWebhook: async () => {
    const response = await authenticatedFetch(`${API_BASE_URL}/webhooks`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  generateWebhook: async (type) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/webhooks/${type}`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  generateTelegramReceiptScannerWebhook: async () => webhookAPI.generateWebhook('telegram-receipt-scanner'),

  updateWebhookStatus: async (webhookId, isActive) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/webhooks/${webhookId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_active: isActive }),
    });
    return handleResponse(response);
  },

  deleteWebhook: async (webhookId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
    return handleResponse(response);
  },
};

// File APIs (OSS)
export const fileAPI = {
  uploadClaimItemFile: async (claimId, itemId, file) => {
    // Wrapped in network-retry: large claims upload one receipt per request, and a
    // single dropped connection mid-burst used to abort the whole submission
    // ("Failed to fetch"). Retry recovers transient drops; FormData is rebuilt each
    // attempt because a consumed body cannot be replayed.
    return withNetworkRetry(async () => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await authenticatedFetch(`${API_BASE_URL}/files/claims/${claimId}/items/${itemId}/upload`, {
        method: 'POST',
        body: formData,
      });
      return handleResponse(response);
    });
  },

  getClaimFiles: async (claimId) => {
    const response = await authenticatedFetch(`${API_BASE_URL}/files/claims/${claimId}`);
    return handleResponse(response);
  },

  analyzeReceipts: async (files = []) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await authenticatedFetch(`${API_BASE_URL}/files/analyze`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },

  analyzeReceiptsViaN8n: async (files = []) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    const response = await authenticatedFetch(`${API_BASE_URL}/files/analyze-via-n8n`, {
      method: 'POST',
      body: formData,
    });
    return handleResponse(response);
  },
};

export default {
  auth: authAPI,
  user: userAPI,
  claim: claimAPI,
  audit: auditAPI,
  quota: quotaAPI,
  company: companyAPI,
  department: departmentAPI,
  expense: expenseAPI,
  expenseGlCode: expenseGlCodeAPI,
  webhook: webhookAPI,
  file: fileAPI,
};
